/**
 * Category navigation data from GET /games/categories.
 *
 * The backend serves ONE per-scope list (X-Request-Language and
 * X-Request-Country ride along automatically via services/api.js):
 * { api_slug, route_slug, label, show_in_left, show_in_horizontal,
 *   sort_horizontal, icon_left, icon_horizontal, synonyms }.
 *
 * Contract rules (backend PR "categories API contract"):
 * - Array order IS the left-menu order. Never re-sort it client-side;
 *   the server tiebreaks are not reproducible from the payload.
 * - The horizontal strip filters on show_in_horizontal (truthy) and sorts
 *   on sort_horizontal, preserving array order on ties (Array.sort is
 *   spec-stable).
 * - Labels arrive translated per scope; render them VERBATIM, never
 *   through __() (only local fallback labels go through the translator).
 *
 * On any failure this module returns null and every consumer keeps
 * rendering its existing hardcoded configuration (unchanged for Games.js;
 * CategoryGameContainer's fallback now shares this map, which also corrects
 * its old bonus_buys typo to bonus_buy). The 'search' and 'provably-fair'
 * pseudo-categories are client concepts the API never serves; they live
 * where they always lived.
 */

import { gameTypeAPISlugs as centralRouteApiSlugs } from "configs/gameTypes";

export const fetchCategories = async (api) => {
  var response = await api.get("/games/categories");
  if (
    response.status === 200 &&
    response.data &&
    Array.isArray(response.data.categories) &&
    response.data.categories.length
  ) {
    return response.data.categories;
  }
  return null;
};

/**
 * Route-slug to API-slug fallback, used ONLY when the categories API is
 * unavailable (fetchCategories returned null). This used to be copy-pasted
 * inline into Games.js and CategoryGameContainer.js, where the two copies had
 * already drifted (bonus_buy vs the broken bonus_buys). We derive it from the
 * central configs/gameTypes map (already the icon/title source of truth) and
 * add only the route keys that map lacks but the games pages resolve:
 * blackjack, video-poker, and the api-slug-keyed aliases. We intentionally do
 * NOT add these to configs/gameTypes itself: that map feeds a reverse lookup
 * (getGamesPageCategoryPath) and a second table_games entry there would flip
 * its result. 'search' and 'provably-fair' are client-only pseudo-categories.
 */
export const LOCAL_ROUTE_FALLBACK = {
  ...centralRouteApiSlugs,
  blackjack: "blackjack",
  "video-poker": "table_games",
  table_games: "table_games",
  video_slots: "video_slots",
  other: "other",
};

/** Resolve a route slug to its API slug: the live categories list wins (a
 *  BO-added category is unknown to LOCAL_ROUTE_FALLBACK), then the local map. */
export const routeToApiSlug = (categories, routeSlug) =>
  categoryByRoute(categories, routeSlug)?.api_slug ?? LOCAL_ROUTE_FALLBACK[routeSlug];

export const categoryByRoute = (categories, routeSlug) => {
  if (!Array.isArray(categories)) {
    return null;
  }
  for (var i = 0; i < categories.length; i++) {
    if (categories[i].route_slug === routeSlug) {
      return categories[i];
    }
  }
  return null;
};

export const categoryByApiSlug = (categories, apiSlug) => {
  if (!Array.isArray(categories)) {
    return null;
  }
  for (var i = 0; i < categories.length; i++) {
    if (categories[i].api_slug === apiSlug) {
      return categories[i];
    }
  }
  return null;
};

export const horizontalItems = (categories) => {
  if (!Array.isArray(categories)) {
    return null;
  }
  return categories
    // Virtual links are a left-menu feature; they have no route and must not
    // appear in the horizontal games strip (which builds /games/<route_slug>).
    // Section headers have route_slug null, so they must never become a tab
    // either (the back office guards this too; this is the belt to that brace).
    .filter((category) => !!category.show_in_horizontal && category.menu_type !== "link" && !category.is_group)
    .sort((a, b) => a.sort_horizontal - b.sort_horizontal);
};

/**
 * Categories matching a search query on their label or their BO-entered
 * search synonyms (string, comma-separated, or array). Sections (no route)
 * and virtual links are never returned, nor is a category hidden from both
 * menus. Case-insensitive substring match, capped at 5 results.
 */
export const searchCategories = (categories, query) => {
  if (!Array.isArray(categories)) {
    return [];
  }
  var q = String(query || "").trim().toLowerCase();
  if (q.length < 2) {
    return [];
  }
  var terms = function (c) {
    var t = [c.label];
    if (Array.isArray(c.synonyms)) {
      t = t.concat(c.synonyms);
    } else if (typeof c.synonyms === "string" && c.synonyms) {
      t = t.concat(c.synonyms.split(","));
    }
    return t;
  };
  return categories
    .filter(function (c) {
      if (c.is_group || c.menu_type === "link") {
        return false;
      }
      if (!c.show_in_left && !c.show_in_horizontal) {
        return false;
      }
      return terms(c).some(function (t) {
        return String(t || "").trim().toLowerCase().indexOf(q) !== -1;
      });
    })
    .slice(0, 5);
};

/** Left-menu visibility for a category the sidebar knows by api_slug;
 *  unknown slugs stay visible (the sidebar's own curation decides). */
export const leftVisible = (categories, apiSlug) => {
  var category = categoryByApiSlug(categories, apiSlug);
  return category ? !!category.show_in_left : true;
};

/**
 * Build the 2-level left-menu tree from the categories payload.
 *
 * The payload has no per-item id, so nesting is read from ARRAY ADJACENCY: the
 * server emits every top-level row immediately followed by its children (a null
 * parent_id starts a new section, a non-null parent_id attaches to the section
 * that opened it). That is the authoritative left-menu order; never re-sort it.
 *
 * Returns an array of sections, each:
 *   { apiSlug, label, routeSlug, iconLeft, isGroup, children: [ ...leaves ] }
 * Hidden rows (show_in_left falsy) are dropped, as are header-only groups left
 * with no visible children. Returns null when there is no list, so every
 * consumer can fall back to its existing hardcoded menu byte-identically.
 */
export const buildSidebarTree = (categories) => {
  if (!Array.isArray(categories)) {
    return null;
  }

  var node = function (c) {
    return {
      apiSlug: c.api_slug,
      label: c.label,
      routeSlug: c.route_slug,
      iconLeft: c.icon_left || null,
      iconKey: c.icon_key || null,
      iconEmoji: c.icon_emoji || null,
      badge: c.badge || null,
      accent: c.accent || null,
      isFeatured: !!c.is_featured,
      menuType: c.menu_type === "link" ? "link" : "category",
      linkUrl: c.link_url || null,
      isGroup: !!c.is_group,
      showInLeft: !!c.show_in_left,
    };
  };

  var sections = [];
  var current = null;
  for (var i = 0; i < categories.length; i++) {
    var c = categories[i];
    if (c.parent_id === null || c.parent_id === undefined) {
      current = node(c);
      current.children = [];
      sections.push(current);
    } else if (current) {
      current.children.push(node(c));
    }
  }

  // A link item without a destination has nowhere to go; render nothing
  // rather than a dead /games/<slug> route (the BO requires the URL, this
  // guards malformed rows).
  var hasDestination = function (n) {
    return !(n.menuType === "link" && !n.linkUrl);
  };

  return sections
    .map(function (s) {
      return Object.assign({}, s, {
        children: s.children.filter(function (child) {
          return child.showInLeft && hasDestination(child);
        }),
      });
    })
    .filter(function (s) {
      return s.showInLeft && hasDestination(s);
    })
    .filter(function (s) {
      // A header-only group with every child hidden has nothing to show.
      return !(s.isGroup && s.children.length === 0);
    });
};
